import React, { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Box, Button, Stack } from '../';

const Card = lazy(() => import('./assets/card.svg?react'));

// Constants for better maintainability
const MOBILE_SVG_WIDTH = 320;
const DESKTOP_SVG_WIDTH = 650;
const ERROR_ICON_SIZE = 64;
const DEFAULT_MAX_WIDTH = 300;

export interface DashboardErrorProps {
  /** Title text for the error message */
  title?: string;
  /** Detailed error message */
  message?: string;
  /** Whether to show the general error layout with illustration */
  general?: boolean;
  /** Callback function when retry button is clicked */
  onRetry?: () => void;
}

const DashboardError: React.FC<DashboardErrorProps> = ({
  title = 'Unable to load data',
  message = 'There was an error loading your data. Please try again.',
  general = false,
  onRetry,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['hubCommon']);

  const displayMessage = useMemo(() => {
    if (message.toLowerCase() === 'failed') {
      return t('failed');
    }
    return message;
  }, [message, t]);

  const svgWidth = isMobile ? MOBILE_SVG_WIDTH : DESKTOP_SVG_WIDTH;
  const titleMaxWidth = isMobile ? '80vw' : 'none';
  const subtitleMaxWidth = isMobile ? '60vw' : 'none';
  return (
    <Box
      component="section"
      role="alert"
      aria-live="polite"
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
      }}
    >
      <Stack alignItems="center" spacing={4}>
        {general ? (
          <>
            <Suspense fallback={null}>
              <Card
                style={{
                  width: svgWidth,
                  height: 'auto',
                  maxWidth: '100%',
                }}
                aria-hidden="true"
              />
            </Suspense>
            <Stack alignItems="center" spacing={2}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                  maxWidth: titleMaxWidth,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                  maxWidth: subtitleMaxWidth,
                }}
              >
                {displayMessage}
              </Typography>
            </Stack>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: ERROR_ICON_SIZE,
                height: ERROR_ICON_SIZE,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (theme) => theme.palette.error.main,
                color: (theme) => theme.palette.common.white,
              }}
              aria-hidden="true"
            >
              <SystemIcon iconName="Warning" fontSize="large" />
            </Box>
            <Stack alignItems="center" spacing={2}>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  color: (theme) => theme.palette.text.main,
                  fontWeight: (theme) => theme.typography.fontWeightMedium,
                  textAlign: 'center',
                  maxWidth: titleMaxWidth,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.second,
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                  maxWidth: isMobile ? subtitleMaxWidth : DEFAULT_MAX_WIDTH,
                }}
              >
                {displayMessage}
              </Typography>
            </Stack>
          </>
        )}

        {onRetry && (
          <Button
            variant="primary"
            onClick={onRetry}
            aria-label="Retry loading data"
          >
            {t('error.try_again')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default DashboardError;
